import React, { createContext, useContext, useState, useEffect } from 'react';
import './CustomModal.css';

export const CustomModalContext = createContext();

let globalShowModal = null;

export const customModal = {
  alert: (title, message, type = 'info') => {
    if (globalShowModal) {
      return new Promise((resolve) => {
        globalShowModal({
          title,
          message,
          type,
          confirmText: 'OK',
          showCancel: false,
          onConfirm: () => resolve(true),
        });
      });
    } else {
      window.alert(`${title}\n${message}`);
    }
  },
  confirm: (title, message, options = {}) => {
    if (globalShowModal) {
      return new Promise((resolve) => {
        globalShowModal({
          title,
          message,
          type: options.type || 'warning',
          confirmText: options.confirmText || 'Confirm',
          cancelText: options.cancelText || 'Cancel',
          showCancel: true,
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
    } else {
      return Promise.resolve(window.confirm(`${title}\n${message}`));
    }
  },
};

export const CustomModalProvider = ({ children }) => {
  const [modalConfig, setModalConfig] = useState(null);

  useEffect(() => {
    globalShowModal = (config) => setModalConfig(config);
    return () => {
      globalShowModal = null;
    };
  }, []);

  const handleClose = (isConfirmed) => {
    if (!modalConfig) return;
    if (isConfirmed && modalConfig.onConfirm) {
      modalConfig.onConfirm();
    } else if (!isConfirmed && modalConfig.onCancel) {
      modalConfig.onCancel();
    }
    setModalConfig(null);
  };

  const getIcon = (type = 'info') => {
    switch (type) {
      case 'success':
        return (
          <div className="custom-modal-icon-badge success">
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="custom-modal-icon-badge error">
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="custom-modal-icon-badge warning">
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
        );
      case 'confirm':
      default:
        return (
          <div className="custom-modal-icon-badge info">
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
        );
    }
  };

  return (
    <CustomModalContext.Provider value={{ showModal: setModalConfig }}>
      {children}
      {modalConfig && (
        <div className="custom-modal-overlay" onClick={() => handleClose(false)}>
          <div className="custom-modal-card" onClick={(e) => e.stopPropagation()}>
            {getIcon(modalConfig.type)}
            <h3 className="custom-modal-title">{modalConfig.title}</h3>
            {modalConfig.message && (
              <p className="custom-modal-message">{modalConfig.message}</p>
            )}
            <div className="custom-modal-actions">
              {modalConfig.showCancel !== false && (
                <button
                  type="button"
                  className="custom-modal-btn cancel"
                  onClick={() => handleClose(false)}
                >
                  {modalConfig.cancelText || 'Cancel'}
                </button>
              )}
              <button
                type="button"
                className={`custom-modal-btn confirm ${modalConfig.type || 'primary'}`}
                onClick={() => handleClose(true)}
              >
                {modalConfig.confirmText || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </CustomModalContext.Provider>
  );
};

export const useCustomModal = () => useContext(CustomModalContext);
